import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MIcon } from '../../lib/micon';
import { Schueler, DbNote, VorjahresnoteRow } from '../../types';
import { useSchueler } from '../../contexts/SchuelerContext';
import { useEscapeKey, useIsMobile } from '../../lib/hooks';
import { API_BASE, apiHeaders, apiGet, apiPost, apiPut, apiDelete, zeigeApiFehler, leseFallback } from '../../lib/api';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';
import { oeffneMailVerfassen, nimmGespraechAusTermin } from '../../lib/nav';
import { fruehereSchuljahre, schuljahrKurz } from '../../lib/schuljahr';
import { NotizenProfilKarte } from '../notizen/NotizenProfilKarte';
import { Notiz, ladeNotizen, NOTIZEN_RELOAD } from '../notizen/notizen-lib';
import { SchuelerBearbeitenModal } from './SchuelerBearbeitenModal';
import { AuditModal } from './AuditModal';
import { ManuelleFehlzeitModal } from '../fehlzeiten/FehlzeitenScreen';
import { LdSheet } from '../../components/ui/LdSheet';
import { Button } from '../../components/ld/Button';
import { ElternErstellenModal } from '../../components/eltern/ElternErstellenModal';
import { elternApi } from '../../components/eltern/eltern-api';
import type { ProfilZiel } from '../../types';
import { TerminVorschlagenModal } from '../slot-picking/TerminVorschlagenModal';
import { FehlzeitenVorjahreProfil } from '../fehlzeiten/FehlzeitenVorjahre';
import { FehlzeitenEintraege } from '../fehlzeiten/FehlzeitenEintraege';
import { NoteSheet } from '../noten/NoteSheet';
import { ZeugnisTextSheet } from './profil/ZeugnisTextSheet';
import './U6Bearbeiten.css';
import { ProfilKopf, SchnellAktion } from './profil/ProfilKopf';
import { ProfilUeberblick, ProfilStartMobil, ElternBereich, ProfilTab, VerlaufListe } from './profil/ProfilUeberblick';
import { EintragenSheet, AktionsLeiste } from './profil/Eintragen';
import { NotenTab } from './profil/NotenTab';
import { MitarbeitTab } from './profil/MitarbeitTab';
import { GespraecheTab } from './profil/GespraecheTab';
import { GespraechSheet } from './profil/GespraechSheet';
import { AttestpflichtZeile, AttestpflichtSheet } from './profil/Attestpflicht';
import type { Gespraech, ElternKontakt, Ueberblick, GespraechVorlage } from './profil/profil-api';
import { MitarbeitWocheSheet } from './profil/MitarbeitWocheSheet';
import { useUeberblick } from './profil/profil-api';
import * as fmt from './profil/format';
import { NotizSheet } from '../notizen/NotizSheet';
import type { ElternEintrag } from '../../components/eltern/eltern-api';
import './profil/profil.css';
import './profil/profil-tabs.css';
import './profil/profil-p1d.css';
import { LdAuswahl, LdDatum, LdFensterFuss } from '../../components/ld/LdFenster';
export function SchuelerProfil({ schueler: s, onBack, startZiel, onBereich }: {
  schueler: Schueler; onBack: () => void; startZiel?: ProfilZiel;
  /** Welle P1d: am Handy ist eine Bereich-Seite offen (die Klasse blendet dann vor/zurück aus) */
  onBereich?: (offen: boolean) => void;
}) {
  const { patchSchueler, entferneSchuelerLokal } = useSchueler();
  const isMobile = useIsMobile();
  // Esc: aus einer Bereich-Seite zurück zum Profil-Start, sonst aus dem Profil
  useEscapeKey(() => { if (isMobile && profilTabRef.current !== 'ueberblick') bereichZurueck(); else onBack(); }, { seite: true });
  const [bearbeitenOffen, setBearbeitenOffen] = useState(false);
  const [profilFehlzeitOffen, setProfilFehlzeitOffen] = useState(false);
  const [fzListeKey, setFzListeKey] = useState(0);   // Welle U3b: Eintragsliste neu laden
  // Welle P1b: Überblick (Kopf-Chips, Kennzahlen, Steht an, Eltern) — alle Noten vom Server
  const ub = useUeberblick(s.id);
  // Welle U4b: Notizen des Schülers aus der Datenbank (Profil-Karte); P1b: Fehler ≠ leer
  const [profilNotizen, setProfilNotizen] = useState<Notiz[] | null>(null);
  const [notizenFehler, setNotizenFehler] = useState(false);
  const notizLauf = useRef(0);
  const ladeProfilNotizen = React.useCallback(() => {
    const nr = ++notizLauf.current;
    ladeNotizen({ schueler_id: s.id, limit: 500 })
      .then(r => { if (nr === notizLauf.current) { setProfilNotizen(r.notizen); setNotizenFehler(false); } })
      .catch(() => { if (nr === notizLauf.current) setNotizenFehler(true); });
  }, [s.id]);
  useEffect(() => {
    setProfilNotizen(null); setNotizenFehler(false); ladeProfilNotizen();
    const lade = () => { ladeProfilNotizen(); ub.neuLaden(); };
    const nachDiktat = () => { lade(); setFzListeKey(k => k + 1); };
    window.addEventListener(NOTIZEN_RELOAD, lade);
    window.addEventListener('ld-diktat-gespeichert', nachDiktat);
    return () => { window.removeEventListener(NOTIZEN_RELOAD, lade); window.removeEventListener('ld-diktat-gespeichert', nachDiktat); };
  }, [ladeProfilNotizen]); // eslint-disable-line react-hooks/exhaustive-deps
  const [elternNeuOffen, setElternNeuOffen] = useState(false);
  const [elternEdit, setElternEdit] = useState<ElternEintrag | null>(null);
  const [notizNeuOffen, setNotizNeuOffen] = useState(false);
  const [mitarbeitWoche, setMitarbeitWoche] = useState<{ fach: string; woche?: string } | null>(null);
  // Welle U2: Elternmail → Verfassen an den Hauptkontakt (A1), sonst erster
  // verknüpfter Kontakt mit Adresse, sonst Alt-Spalte eltern_email.
  async function elternmailOeffnen() {
    let adresse: string | null = null;
    try {
      const { eltern } = await elternApi.laden(s.id);
      const mitMail = eltern.filter(e => e.email);
      adresse = (mitMail.find(e => e.ist_hauptkontakt === 1) ?? mitMail[0])?.email ?? null;
    } catch { /* Rückfall unten */ }
    if (!adresse && s.eltern_email) adresse = s.eltern_email;
    if (adresse) { oeffneMailVerfassen(adresse, s.name); return; }
    if (await confirm('Für diese Schülerin bzw. diesen Schüler ist keine Eltern-Adresse gespeichert.', {
      title: 'Kein Elternkontakt hinterlegt', confirmLabel: 'Elternkontakt anlegen', cancelLabel: 'Abbrechen',
    })) setElternNeuOffen(true);
  }
  const [verlassenDialog, setVerlassenDialog] = useState(false);
  const [verlassenLaden, setVerlassenLaden] = useState(false);
  const [terminModalOffen, setTerminModalOffen] = useState(false);

  async function schuelerVerlassen() {
    setVerlassenLaden(true);
    try {
      await apiPost(`/api/schueler/${s.id}/verlassen`);
      entferneSchuelerLokal(s.id);
      window.dispatchEvent(new Event('lehrerdesk-schueler-reload'));
      toast(`${s.name} wurde als inaktiv markiert`, 'success');
      onBack();
    } catch (err) { zeigeApiFehler(err, 'Aktion fehlgeschlagen'); }
    finally { setVerlassenLaden(false); setVerlassenDialog(false); }
  }
  // DB-Noten für diesen Schüler
  const [dbNoten,    setDbNoten]    = useState<DbNote[]>([]);

  useEffect(() => {
    fetch(`${API_BASE}/api/noten?schueler_id=${s.id}`, { headers: apiHeaders() }).then(leseFallback([], '/api/noten'))
      .then(setDbNoten).catch(() => {});
  }, [s.id]);

  // Vorjahresnoten
  const [vorjahresNoten, setVorjahresNoten] = useState<VorjahresnoteRow[]>([]);

  const [vjModalOffen, setVjModalOffen] = useState(false);
  const [vjFach, setVjFach] = useState('');
  // Welle N2: Schuljahr wird gewählt (JJJJ/JJJJ), nicht getippt — nur frühere Schuljahre
  const vjSchuljahre = fruehereSchuljahre();
  const [vjSchuljahr, setVjSchuljahr] = useState(() => vjSchuljahre[0]);
  const [vjHalbjahr, setVjHalbjahr] = useState<1 | 2>(1);
  const [vjNote, setVjNote] = useState('3');
  const [vjAvNote, setVjAvNote] = useState('');
  const [vjSvNote, setVjSvNote] = useState('');
  const [vjSpeichern, setVjSpeichern] = useState(false);

  // Note-Sheet (Welle U6a: gemeinsames NoteSheet für Anlegen und Bearbeiten)
  const [noteModalOffen, setNoteModalOffen] = useState(false);
  const [noteFach, setNoteFach] = useState('');
  const [noteEdit, setNoteEdit] = useState<DbNote | null>(null);
  const noteHalbjahr: 'HJ1' | 'HJ2' = (() => {
    const m = new Date().getMonth() + 1;
    return (m >= 8 || m <= 1) ? 'HJ1' : 'HJ2';
  })();
  const noteSchuljahr = (() => {
    const m = new Date().getMonth() + 1;
    const y = new Date().getFullYear();
    return m >= 8 ? `${y}/${y + 1}` : `${y - 1}/${y}`;
  })();

  const ladeVorjahresNoten = () => {
    fetch(`${API_BASE}/api/vorjahres-noten/${s.id}`, { headers: apiHeaders() })
      .then(leseFallback([] as VorjahresnoteRow[], '/api/vorjahres-noten'))
      .then((rows: VorjahresnoteRow[]) => setVorjahresNoten(rows))
      .catch(() => {});
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { ladeVorjahresNoten(); }, [s.id]);
  const letzteNoteRef = React.useRef<DbNote | null>(null);
  useEffect(() => { if (noteEdit) letzteNoteRef.current = noteEdit; }, [noteEdit]);


  async function vorjahresnoteLoeschen(id: number) {
    try {
      await apiDelete(`/api/vorjahres-noten/${id}`);
      ladeVorjahresNoten();
      ub.neuLaden(); // Vorjahr im Noten-Tab (Information) neu
    } catch (err) { zeigeApiFehler(err, 'Löschen fehlgeschlagen'); }
  }

  async function vorjahresnoteEinreichen() {
    if (!vjFach.trim() || !vjNote) return;
    setVjSpeichern(true);
    try {
      await apiPost('/api/vorjahres-noten', {
        schueler_id: s.id,
        fach: vjFach.trim(),
        schuljahr: vjSchuljahr,
        halbjahr: vjHalbjahr,
        zeugnisnote: Number(vjNote),
        av_note: vjAvNote ? Number(vjAvNote) : null,
        sv_note: vjSvNote ? Number(vjSvNote) : null,
      });
      setVjModalOffen(false);
      setVjFach(''); setVjNote('3'); setVjAvNote(''); setVjSvNote('');
      ladeVorjahresNoten();
      ub.neuLaden(); // Vorjahr im Noten-Tab (Information) neu
    } catch (err) { zeigeApiFehler(err, 'Speichern fehlgeschlagen'); }
    finally { setVjSpeichern(false); }
  }

  // Welle P1b: offene Punkte stehen in „Steht an“ (Überblick); hier nur noch „+ Punkt“
  const [neuerPunktModal, setNeuerPunktModal] = useState(false);
  const [npTyp, setNpTyp] = useState('nachschreiben');
  const [npTitel, setNpTitel] = useState('');
  const [npFach, setNpFach] = useState('');
  const [npFaellig, setNpFaellig] = useState('');
  const [npSpeichern, setNpSpeichern] = useState(false);

  // Attestpflicht
  const [attestAktiv, setAttestAktiv] = useState(s.attestpflicht === 1);
  const [attestSeit, setAttestSeit] = useState(s.attestpflicht_seit ?? '');

  // Zeugnis-Text (Welle B2: ZeugnisTextSheet lädt, erzeugt und speichert selbst)
  const [zeugnisModal, setZeugnisModal] = useState(false);
  const zeugnisGenerieren = () => setZeugnisModal(true);
  const [attestNotiz, setAttestNotiz] = useState(s.attestpflicht_notiz ?? '');
  const [attestSheetOffen, setAttestSheetOffen] = useState(false);

  // Welle P1c: Attestpflicht aus dem Sheet (Statuszeile im Fehlzeiten-Tab)
  async function speichereAttest(aktiv: boolean, seit: string, notiz: string): Promise<boolean> {
    try {
      await apiPut(`/api/schueler/${s.id}`, { attestpflicht: aktiv, attestpflicht_seit: seit || null, attestpflicht_notiz: notiz || null });
      // Lokaler State erst NACH bestätigtem Server-Erfolg (M6-Fix)
      setAttestAktiv(aktiv); setAttestSeit(seit); setAttestNotiz(notiz);
      patchSchueler(s.id, prev => ({ ...prev, attestpflicht: aktiv ? 1 : 0, attestpflicht_seit: seit, attestpflicht_notiz: notiz }));
      ub.neuLaden(); setFzListeKey(k => k + 1);
      toast('Attestpflicht gespeichert', 'success');
      return true;
    } catch (err) {
      zeigeApiFehler(err, 'Speichern fehlgeschlagen');
      return false;
    }
  }

  async function speichereNeuenPunkt() {
    if (!npTitel.trim()) return;
    setNpSpeichern(true);
    try {
      await apiPost(`/api/schueler/${s.id}/offene-punkte`, { typ: npTyp, titel: npTitel, faellig_am: npFaellig || null, fach: npFach || null });
      ub.neuLaden(); setNeuerPunktModal(false); setNpTitel(''); setNpFach(''); setNpFaellig('');
      toast('Punkt angelegt', 'success');
    } catch (err) { zeigeApiFehler(err, 'Speichern fehlgeschlagen'); }
    finally { setNpSpeichern(false); }
  }

  // Welle P1b: Kopf-Kennzahlen (Fehlzeiten, §68, Attestpflicht) kommen aus dem Überblick (Server);
  // Geschlecht steht nicht mehr im Kopf (bleibt in den Stammdaten).


  const verfuegbareFaecher = useMemo(() => {
    const aus = Array.from(new Set(dbNoten.map(n => n.fach)));
    return aus.length > 0 ? aus : ['Deutsch', 'Mathematik', 'Englisch'];
  }, [dbNoten]);

  // Welle U2: Start-Tab aus einem Profil-Sprung ('eltern' = am PC Überblick + Eltern-Liste,
  // Welle P1d: am Handy die Bereich-Seite Eltern)
  const [profilTab, setProfilTab] = useState<ProfilTab>(
    () => !startZiel ? 'ueberblick' : startZiel === 'eltern' ? (window.innerWidth < 1024 ? 'eltern' : 'ueberblick') : startZiel);
  const profilTabRef = useRef(profilTab);
  profilTabRef.current = profilTab;
  const bereichOffen = isMobile && profilTab !== 'ueberblick';
  const [eintragenOffen, setEintragenOffen] = useState(false);
  // Welle P1b: ein neu geöffnetes Profil (auch vor/zurück in der Klasse) beginnt oben
  const wurzelRef = useRef<HTMLDivElement>(null);
  function nachOben() {
    window.scrollTo(0, 0);
    for (let el = wurzelRef.current?.parentElement; el; el = el.parentElement) {
      const o = getComputedStyle(el).overflowY;
      if ((o === 'auto' || o === 'scroll') && el.scrollTop > 0) el.scrollTop = 0;
    }
  }
  React.useLayoutEffect(() => {
    if (startZiel === 'eltern' && !isMobile) return;
    nachOben();
  }, [s.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Welle P1d: Bereich-Seiten am Handy mit eigenem Verlaufseintrag ──────────────
  // Öffnen legt EINEN Eintrag an (Wechsel zwischen Bereichen ersetzt ihn — höchstens eine Ebene);
  // ‹ und die Zurück-Taste gehen beide über history.back(), geschlossen wird nur im popstate.
  const neuerEintrag = (tab: ProfilTab) => ({ ldScreen: window.history.state?.ldScreen, ldBereich: tab });
  function zuTab(tab: ProfilTab, anker?: string) {
    const springe = (ziel: HTMLElement | null) => setTimeout(() => ziel?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
    if (!isMobile) {
      setProfilTab(tab);
      setTimeout(() => {
        const ziel = (anker && document.getElementById(anker)) || tabLeisteRef.current;
        ziel?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
      return;
    }
    if (tab === 'ueberblick') { bereichZurueck(); return; }
    if (window.history.state?.ldBereich) window.history.replaceState(neuerEintrag(tab), '', window.location.pathname);
    else window.history.pushState(neuerEintrag(tab), '', window.location.pathname);
    setProfilTab(tab);
    nachOben();
    if (anker) springe(document.getElementById(anker));
  }
  function bereichZurueck() {
    if (window.history.state?.ldBereich) window.history.back();
    else setProfilTab('ueberblick');
  }
  // Start-Ziel (Sprung in einen Bereich) am Handy: Eintrag einmal anlegen (Ref schützt vor Doppel-Effekt)
  const startEintrag = useRef(false);
  useEffect(() => {
    if (startEintrag.current || !isMobile || profilTab === 'ueberblick' || window.history.state?.ldBereich) return;
    startEintrag.current = true;
    window.history.pushState(neuerEintrag(profilTab), '', window.location.pathname);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const zurueck = (e: PopStateEvent) => {
      const b = e.state?.ldBereich as ProfilTab | undefined;
      if (b) setProfilTab(b);
      else if (profilTabRef.current !== 'ueberblick' && window.innerWidth < 1024) setProfilTab('ueberblick');
    };
    window.addEventListener('popstate', zurueck);
    return () => window.removeEventListener('popstate', zurueck);
  }, []);
  useEffect(() => { onBereich?.(bereichOffen); }, [bereichOffen]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onBereich?.(false), []); // eslint-disable-line react-hooks/exhaustive-deps
  // Globaler Mikro-Knopf (ld-fab) ausblenden, solange das Profil seine eigene Leiste zeigt
  useEffect(() => {
    if (!isMobile) return;
    document.body.classList.add('ld-profil-offen');
    return () => document.body.classList.remove('ld-profil-offen');
  }, [isMobile]);

  // Welle P1d: Eintragen-Sheet → bestehendes Sheet erst nach dem Schließen öffnen (Sheet-Stapel)
  function eintragenWahl(a: SchnellAktion) {
    setEintragenOffen(false);
    setTimeout(() => schnellAktion(a), 320);
  }
  function eintragenErzaehlen() {
    ueberKindErzaehlen();          // im Klick selbst (iOS: Mikrofon nur aus der Geste heraus)
    setEintragenOffen(false);
  }
  // Schnellaktionen (Eintragen): jedes Sheet mit diesem Kind vorbelegt
  function schnellAktion(a: SchnellAktion) {
    if (a === 'notiz') setNotizNeuOffen(true);
    else if (a === 'fehlzeit') setProfilFehlzeitOffen(true);
    else if (a === 'mitarbeit') setMitarbeitWoche({ fach: ub.daten?.kopf.faecher_bei_mir[0] ?? '' });
    else if (a === 'note') { setNoteFach(ub.daten?.kopf.faecher_bei_mir[0] ?? verfuegbareFaecher[0] ?? ''); setNoteModalOffen(true); }
    else if (a === 'gespraech') setGespraechSheet({ g: null });
  }
  // „Über … erzählen“: Universaldiktat mit diesem Kind als Kontext (KI ordnet zu, Lehrkraft bestätigt)
  function ueberKindErzaehlen() {
    const vorname = ub.daten?.kopf.vorname ?? s.name.split(' ')[0];
    window.dispatchEvent(new CustomEvent('ld-universaldiktat', { detail: { kind: { id: s.id, name: s.name, vorname } } }));
  }
  useEffect(() => {
    if (startZiel !== 'eltern' || isMobile) return;
    const t = setTimeout(() => document.getElementById('ld-profil-eltern')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 350);
    return () => clearTimeout(t);
  }, [startZiel]); // eslint-disable-line react-hooks/exhaustive-deps
  const tabLeisteRef = useRef<HTMLDivElement>(null);
  const [vjAnkerOffen, setVjAnkerOffen] = useState(false);  // Welle B: aufklappbarer Vorjahres-Anker in der Fach-Karte

  type NoteVorschlag = {
    id: number; schueler_id: number; fach: string; schul_woche: string;
    vorgeschlagene_note: number; vorfaelle_anzahl: number; datums: string[];
    bestaetigte_note: number | null;
  };
  const [noteVorschlaege, setNoteVorschlaege] = useState<NoteVorschlag[]>([]);
  const [vorschlagNoten, setVorschlagNoten] = useState<Record<number, number>>({});

  function ladeNoteVorschlaege() {
    fetch(`${API_BASE}/api/mdl-vorschlaege?schueler_id=${s.id}`, { headers: apiHeaders() })
      .then(leseFallback([] as NoteVorschlag[], '/api/mdl-vorschlaege'))
      .then((data: NoteVorschlag[]) => {
        setNoteVorschlaege(data);
        const init: Record<number, number> = {};
        data.forEach(v => { init[v.id] = v.vorgeschlagene_note; });
        setVorschlagNoten(init);
      })
      .catch(() => setNoteVorschlaege([]));
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { ladeNoteVorschlaege(); }, [s.id]);
  const [auditOffen, setAuditOffen] = useState(false);
  // Welle P1c: Gesprächsfenster (neu/bearbeiten) + Nachladen der Gesprächsliste
  const [gespraechSheet, setGespraechSheet] = useState<{ g: Gespraech | null; vorlage?: GespraechVorlage | null } | null>(null);
  const [gespraecheKey, setGespraecheKey] = useState(0);
  // Welle B2: „Gespräch dokumentieren“ aus einem bestätigten Eltern-Termin — Datum, Art und
  // Eltern vorbelegt; ist der Termin schon dokumentiert, öffnet das bestehende Gespräch
  async function gespraechAusTermin(terminId: number) {
    try {
      const v = await apiGet<GespraechVorlage>(`/api/schultermine/${terminId}/gespraech-vorlage`);
      if (v.gespraech_id) setGespraechSheet({ g: await apiGet<Gespraech>(`/api/gespraeche/${v.gespraech_id}`) });
      else setGespraechSheet({ g: null, vorlage: v });
    } catch (err) { zeigeApiFehler(err, 'Termin konnte nicht geladen werden'); }
  }
  async function gespraechNichtNoetig(terminId: number) {
    try {
      await apiPost(`/api/schultermine/${terminId}/gespraech-nicht-noetig`, { an: true });
      ub.neuLaden();
      toast('Als „nicht nötig“ abgehakt', 'success', { dauerMs: 5000, aktion: { label: 'Rückgängig', onClick: async () => {
        try { await apiPost(`/api/schultermine/${terminId}/gespraech-nicht-noetig`, { an: false }); ub.neuLaden(); } catch (err) { zeigeApiFehler(err, 'Rückgängig fehlgeschlagen'); }
      } } });
    } catch (err) { zeigeApiFehler(err, 'Abhaken fehlgeschlagen'); }
  }
  // Sprung aus dem Kalender (nimmGespraechAusTermin): beim Einhängen abholen
  useEffect(() => {
    const z = nimmGespraechAusTermin(s.id);
    if (z != null) gespraechAusTermin(z);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.id]);

  const vorname = ub.daten?.kopf.vorname ?? s.name.split(' ')[0];
  const elternBearbeiten = (e: ElternKontakt) => setElternEdit({ id: e.id, name: e.name, email: e.email, telefon: e.telefon, notiz: e.notiz, rolle: e.rolle, ist_hauptkontakt: e.hauptkontakt ? 1 : 0, eltern_schueler_id: e.eltern_schueler_id });
  // Welle P1d: je Bereich genau EIN Anlegen-Knopf — am PC rechts neben den Reitern, am Handy in der Leiste
  const ANLEGEN: Partial<Record<ProfilTab, { label: string; onClick: () => void }>> = {
    noten:      { label: 'Note',      onClick: () => schnellAktion('note') },
    fehlzeiten: { label: 'Fehlzeit',  onClick: () => schnellAktion('fehlzeit') },
    mitarbeit:  { label: 'Mitarbeit', onClick: () => schnellAktion('mitarbeit') },
    gespraeche: { label: 'Gespräch',  onClick: () => schnellAktion('gespraech') },
    eltern:     { label: 'Kontakt',   onClick: () => setElternNeuOffen(true) },
    notizen:    { label: 'Notiz',     onClick: () => schnellAktion('notiz') },
  };
  const plusKnopf = ANLEGEN[profilTab] ?? null;
  const leistenKnopf = ANLEGEN[profilTab] ?? { label: 'Eintragen', onClick: () => setEintragenOffen(true) };

  // Note-6-Vorschläge: am PC über den Tabs, am Handy auf dem Profil-Start
  const note6 = noteVorschlaege.length > 0 ? (
          <div style={{ marginBottom: 16, padding: '12px 14px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#92400e', marginBottom: 10 }}>
              <MIcon name="warning" size={15} style={{ verticalAlign: '-3px', marginRight: 4 }} />Offene Note-Vorschläge ({noteVorschlaege.length})
            </div>
            {noteVorschlaege.map(v => (
              <div key={v.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '7px 0', borderTop: '1px solid #fed7aa' }}>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: '#78350f' }}>{v.fach}</span>
                  <span style={{ fontSize: 12, color: '#a16207', marginLeft: 6 }}>{v.schul_woche.replace(/^\d{4}-W0?(\d+)$/, 'KW $1')}</span>
                  <span style={{ fontSize: 12, color: '#92400e', marginLeft: 6 }}>· {v.vorfaelle_anzahl}× unentsch.</span>
                </div>
                <LdAuswahl aria-label="Note" style={{ width: 76, minHeight: 36 }} value={vorschlagNoten[v.id] ?? v.vorgeschlagene_note} onChange={e => setVorschlagNoten(prev => ({ ...prev, [v.id]: Number(e.target.value) }))}>
                  {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n}</option>)}
                </LdAuswahl>
                <button
                  onClick={async () => {
                    try {
                      await apiPost(`/api/mdl-vorschlaege/${v.id}/bestaetigen`, { bestaetigte_note: vorschlagNoten[v.id] ?? v.vorgeschlagene_note });
                      toast('Note eingetragen', 'success'); ladeNoteVorschlaege(); ub.neuLaden();
                    } catch (err) { zeigeApiFehler(err, 'Bestätigen fehlgeschlagen'); }
                  }}
                  style={{ padding: '4px 10px', background: 'var(--ld-primary)', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >Bestätigen</button>
                <button
                  onClick={async () => {
                    try {
                      await apiPost(`/api/mdl-vorschlaege/${v.id}/verwerfen`);
                      ladeNoteVorschlaege();
                    } catch (err) { zeigeApiFehler(err, 'Verwerfen fehlgeschlagen'); }
                  }}
                  style={{ padding: '4px 10px', background: 'var(--ld-bg-input)', color: 'var(--ld-text-secondary)', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >Verwerfen</button>
              </div>
            ))}
          </div>
  ) : null;

  return (
    <div ref={wurzelRef} className={`ld-profil-v2${isMobile ? ' ld-profil-v2--leiste' : ''}`} style={{ background: 'transparent', minHeight: '100%' }}>
      {bearbeitenOffen && (
        <SchuelerBearbeitenModal schueler={s} onClose={() => setBearbeitenOffen(false)} />
      )}

      <TerminVorschlagenModal open={terminModalOffen} schuelerId={s.id} onClose={() => setTerminModalOffen(false)} />


      {/* Schüler-verlässt-Klasse Bestätigung */}
      {/* Welle Z3: K4-Rahmen, Text und Ablauf unverändert (umkehrbar → Hauptaktion violett) */}
      <LdSheet open={verlassenDialog} onClose={() => setVerlassenDialog(false)} maxWidth={460}
        kopf={{ titel: 'Schüler verlässt die Klasse?', kontext: s.name }}
        fuss={<LdFensterFuss onAbbrechen={() => setVerlassenDialog(false)} haupt="Schüler verlässt" onHaupt={schuelerVerlassen} laden={verlassenLaden} />}>
        <p style={{ margin: 0, fontSize: 14, color: 'var(--ld-text-primary)' }}><strong>{s.name}</strong> wird inaktiv markiert:</p>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, color: 'var(--ld-text-secondary)', lineHeight: 1.7 }}>
          <li>Erscheint nicht mehr in aktiven Listen</li>
          <li>Profil bleibt im Archiv lesbar</li>
          <li>Alle Noten/Fehlzeiten bleiben erhalten</li>
        </ul>
        <p className="ld-fe-text">Kann im Archiv rückgängig gemacht werden.</p>
      </LdSheet>

      {/* Audit-Verlauf Modal */}
      {auditOffen && <AuditModal schuelerId={s.id} onClose={() => setAuditOffen(false)} />}

      {/* Fehlzeit direkt eintragen (Profil-Tab) */}
      {profilFehlzeitOffen && (
        <ManuelleFehlzeitModal
          schuelerListe={[s]}
          onClose={() => setProfilFehlzeitOffen(false)}
          onEintragen={(name, datum, stunden, tage, entschuldigt, attest) => {
            // Welle U3b: das im Formular gewählte Datum speichern (vorher immer "heute"); Welle B4: attest
            apiPost<{ vorschlaege_erstellt?: number }>('/api/fehlzeiten', {
              schueler_id: s.id, datum: datum || new Date().toISOString().split('T')[0], stunden, tage, entschuldigt, attest: !!attest,
            }).then(data => {
              setFzListeKey(k => k + 1);
              window.dispatchEvent(new Event('lehrerdesk-schueler-reload'));
              // Lokaler State erst NACH bestätigtem Server-Erfolg
              patchSchueler(s.id, prev => ({
                ...prev,
                fehlzeiten: {
                  stunden_gesamt:        prev.fehlzeiten.stunden_gesamt        + stunden,
                  stunden_unentschuldigt: prev.fehlzeiten.stunden_unentschuldigt + (entschuldigt ? 0 : stunden),
                  tage_gesamt:           prev.fehlzeiten.tage_gesamt           + tage,
                  tage_unentschuldigt:   prev.fehlzeiten.tage_unentschuldigt   + (entschuldigt ? 0 : tage),
                },
              }));
              if (!entschuldigt && (data?.vorschlaege_erstellt ?? 0) > 0) {
                toast('Fehlzeit eingetragen · Note-6-Vorschlag erstellt', 'success');
                ladeNoteVorschlaege();
              } else {
                toast('Fehlzeit eingetragen', 'success');
              }
              ub.neuLaden();
            }).catch(err => zeigeApiFehler(err, 'Fehlzeit konnte nicht gespeichert werden'));
            setProfilFehlzeitOffen(false);
          }}
        />
      )}

      {/* Welle P1c: Gespräch dokumentieren (Erzählen/Tippen, Vereinbarungen, Nachfassen) */}
      <GespraechSheet open={!!gespraechSheet} schuelerId={s.id} schuelerName={s.name} vorname={ub.daten?.kopf.vorname ?? s.name.split(' ')[0]}
        eltern={ub.daten?.eltern ?? []} gespraech={gespraechSheet?.g ?? null} vorlage={gespraechSheet?.vorlage ?? null} isMobile={isMobile}
        onClose={() => setGespraechSheet(null)}
        onGespeichert={() => { setGespraecheKey(k => k + 1); ub.neuLaden(); }} />

      {/* Welle B2: Zeugnis-Text gespeichert je Fach und Halbjahr (Sheet statt Inline-Fenster) */}
      <ZeugnisTextSheet open={zeugnisModal} schuelerId={s.id} schuelerName={s.name} klasse={s.klasse} isMobile={isMobile}
        onClose={() => setZeugnisModal(false)} onGeaendert={() => ub.neuLaden()} />

      <style>{`@keyframes slideIn { from { opacity:0; transform:translateX(18px); } to { opacity:1; transform:none; } }`}</style>
      <div style={{ animation: 'slideIn .2s ease-out forwards' }}>
        {/* ── Welle P1b/P1d: Kopf — am Handy nur auf dem Profil-Start ── */}
        {bereichOffen ? <BereichKopf name={s.name} tab={profilTab} daten={ub.daten} onZurueck={bereichZurueck} /> : <ProfilKopf s={s} daten={ub.daten} isMobile={isMobile}
          onAktion={schnellAktion} onDiktat={ueberKindErzaehlen}
          onTermin={() => setTerminModalOffen(true)} onBearbeiten={() => setBearbeitenOffen(true)}
          menu={[
            { label: 'Mail an die Eltern', icon: 'mail', onClick: elternmailOeffnen },
            { label: ub.daten?.zeugnis_texte?.length ? `Zeugnis-Text (${ub.daten.zeugnis_texte.join(', ')} gespeichert)` : 'Zeugnis-Text mit KI', icon: 'auto_awesome', onClick: zeugnisGenerieren },
            { label: 'Änderungsverlauf', icon: 'history', onClick: () => setAuditOffen(true) },
            { label: 'Schüler verlässt die Klasse', icon: 'logout', onClick: () => setVerlassenDialog(true), gefahr: true },
          ]} />}
        <ElternErstellenModal open={elternNeuOffen || !!elternEdit} schuelerId={s.id} editDaten={elternEdit ?? undefined}
          onClose={() => { setElternNeuOffen(false); setElternEdit(null); }}
          onErstellt={() => { setElternNeuOffen(false); setElternEdit(null); ub.neuLaden(); }} />
        <div className="ld-profil-work">

        {/* ── Profil-Tabs (nur PC, nur Text) mit genau einem „+ {Sache}“ rechts — Welle P1d ── */}
        {!isMobile && (
          <div className="ld-profil-tabzeile" style={{ marginTop: 'var(--ld-space-xl)' as any, marginBottom: 'var(--ld-space-xl)' as any }}>
            <div ref={tabLeisteRef} className="ld-profil-tabs" role="tablist">
              {PC_TABS.map(tab => (
                <button key={tab.key} type="button" role="tab" aria-selected={profilTab === tab.key} onClick={() => setProfilTab(tab.key)}
                  className={`ld-profil-tab${profilTab === tab.key ? ' ld-profil-tab--active' : ''}`}>{tab.label}</button>
              ))}
            </div>
            {plusKnopf && <button type="button" className="ld-pk-aktion ld-pk-aktion--primaer ld-profil-plus" onClick={plusKnopf.onClick}><MIcon name="add" size={18} />{plusKnopf.label}</button>}
          </div>
        )}

        {!isMobile && note6}

        <div key={profilTab} className="ld-tab-panel">

        {/* ── Tab: Überblick ── */}
        {profilTab === 'ueberblick' && isMobile && (
          <ProfilStartMobil s={s} daten={ub.daten} fehler={ub.fehler} neuLaden={ub.neuLaden} isMobile onTab={zuTab} hinweise={note6}
            onNeuerPunkt={() => { setNpTyp('sonstiges'); setNpTitel(''); setNpFach(''); setNpFaellig(''); setNeuerPunktModal(true); }}
            onMitarbeitWoche={(fach, woche) => setMitarbeitWoche({ fach, woche })} onTermin={() => setTerminModalOffen(true)}
            onGespraechAusTermin={gespraechAusTermin} onGespraechNichtNoetig={gespraechNichtNoetig}
            onElternNeu={() => setElternNeuOffen(true)} onElternBearbeiten={elternBearbeiten} notizen={null} />
        )}
        {profilTab === 'ueberblick' && !isMobile && (
          <ProfilUeberblick s={s} daten={ub.daten} fehler={ub.fehler} neuLaden={ub.neuLaden} isMobile={isMobile}
            onTab={zuTab}
            onNeuerPunkt={() => { setNpTyp('sonstiges'); setNpTitel(''); setNpFach(''); setNpFaellig(''); setNeuerPunktModal(true); }}
            onMitarbeitWoche={(fach, woche) => setMitarbeitWoche({ fach, woche })}
            onTermin={() => setTerminModalOffen(true)}
            onGespraechAusTermin={gespraechAusTermin} onGespraechNichtNoetig={gespraechNichtNoetig}
            onElternNeu={() => setElternNeuOffen(true)}
            onElternBearbeiten={elternBearbeiten}
            notizen={<NotizenProfilKarte schuelerId={s.id} klasseId={s.klasse_id ?? null} vorname={s.name.split(' ')[0]} notizen={profilNotizen} fehler={notizenFehler} onErneut={ladeProfilNotizen} />} />
        )}

        {/* ── Tab: Noten (Welle P1c: alle Fächer, ehrlich erklärt; Werte nur vom Server) ── */}
        {profilTab === 'noten' && (
          <NotenTab schuelerId={s.id} daten={ub.daten} fehler={ub.fehler} neuLaden={ub.neuLaden} dbNoten={dbNoten}
            onNoteBearbeiten={n => setNoteEdit(n)}
            onAenderungen={() => setAuditOffen(true)}
            onMitarbeitWoche={(fach, woche) => setMitarbeitWoche({ fach, woche })}
            vorjahr={
              <section className="ld-pu-karte ld-nt-vorjahr">
                <button type="button" className="ld-nt-vorjahr__kopf" aria-expanded={vjAnkerOffen} onClick={() => setVjAnkerOffen(o => !o)}>
                  <MIcon name="history_edu" size={19} /><span>Zeugnisnoten aus dem Vorjahr{vorjahresNoten.length ? ` · ${vorjahresNoten.length}` : ''}</span>
                  <MIcon name={vjAnkerOffen ? 'expand_less' : 'expand_more'} size={20} />
                </button>
                {vjAnkerOffen && (
                  <div className="ld-nt-vorjahr__inhalt">
                    <p>Nur zur Information — sie zählen nicht in die Halbjahresnote. Im Halbjahr 2 zählt deine HJ1-Endnote desselben Schuljahrs zur Hälfte.</p>
                    {vorjahresNoten.length === 0 ? <div className="ld-pu-leer">Keine Vorjahresnoten erfasst.</div> : (
                      <ul>{vorjahresNoten.map(vj => (
                        <li key={vj.id}><b>{vj.fach}</b><span>Halbjahr {vj.halbjahr} · {schuljahrKurz(vj.schuljahr)}</span><b className="ld-nt-vorjahr__note">{fmt.note(vj.zeugnisnote).replace(/,0$/, '')}</b>
                          <button type="button" className="ld-pk-icon ld-pk-icon--klein" aria-label={`Vorjahresnote ${vj.fach} löschen`} onClick={() => vorjahresnoteLoeschen(vj.id)}><MIcon name="delete" size={16} /></button></li>))}</ul>
                    )}
                    <button type="button" className="ld-pu-link" onClick={() => setVjModalOffen(true)}><MIcon name="add" size={16} />Vorjahresnote erfassen</button>
                  </div>
                )}
              </section>
            } />
        )}

                {/* ── Tab: Fehlzeiten (Welle U3b: Kennzahlen + Eintragsliste mit Bearbeiten) ── */}
        {profilTab === 'fehlzeiten' && (
          <div>
            <FehlzeitenEintraege
              schuelerId={s.id} schuelerName={s.name}
              attestOffen={s.attestpflicht ? (s.attest_offen ?? 0) : 0} attestSeit={s.attestpflicht_seit ?? null}
              neuLadenKey={fzListeKey}
              onGeaendert={ladeNoteVorschlaege}
              onVerlauf={() => setAuditOffen(true)}
              attestBereich={
                <AttestpflichtZeile aktiv={attestAktiv} seit={attestSeit || null} notiz={attestNotiz || null} onBearbeiten={() => setAttestSheetOffen(true)} />
              }
            />
            <AttestpflichtSheet open={attestSheetOffen} schuelerName={s.name} aktiv={attestAktiv} seit={attestSeit} notiz={attestNotiz}
              onClose={() => setAttestSheetOffen(false)} onSpeichern={speichereAttest} />

            {/* Welle J1: ältere Schuljahre — nur lesen, zählen nicht zu §68 */}
            <FehlzeitenVorjahreProfil vorjahre={s.fehlzeiten_vorjahre} />
          </div>
        )}

        {/* ── Tab: Mitarbeit (Welle P1c: Wochenraster, gleiche Quelle wie die Notengrundlage) ── */}
        {profilTab === 'mitarbeit' && (
          <MitarbeitTab schuelerId={s.id} isMobile={isMobile} halbjahr={ub.daten?.halbjahr.halbjahr ?? (noteHalbjahr === 'HJ1' ? 1 : 2)}
            schuljahr={ub.daten?.halbjahr.schuljahr ?? noteSchuljahr} onGeaendert={() => ub.neuLaden()} />
        )}

        {/* ── Tab: Gespräche (Welle P1c) ── */}
        {profilTab === 'gespraeche' && (
          <GespraecheTab schuelerId={s.id} eltern={ub.daten?.eltern ?? []} neuLadenKey={gespraecheKey} ohneKopf={isMobile}
            termineOffen={ub.daten?.bereiche?.gespraeche.termine_offen ?? []} onAusTermin={gespraechAusTermin}
            onBearbeiten={g => setGespraechSheet({ g })} onGeaendert={() => ub.neuLaden()} />
        )}

        {/* ── Tab: Verlauf (Welle P1c) ── */}
        {profilTab === 'verlauf' && <VerlaufListe schuelerId={s.id} onTab={zuTab} seite={20} mitSchuljahr />}

        {/* ── Welle P1d: Eltern und Notizen als eigene Bereich-Seiten (nur Handy) ── */}
        {profilTab === 'eltern' && (
          <ElternBereich s={s} daten={ub.daten} fehler={ub.fehler} neuLaden={ub.neuLaden} isMobile={isMobile} onTab={zuTab}
            onNeuerPunkt={() => setNeuerPunktModal(true)} onMitarbeitWoche={(fach, woche) => setMitarbeitWoche({ fach, woche })}
            onTermin={() => setTerminModalOffen(true)} onElternNeu={() => setElternNeuOffen(true)} onElternBearbeiten={elternBearbeiten} notizen={null} />
        )}
        {profilTab === 'notizen' && (
          <NotizenProfilKarte schuelerId={s.id} klasseId={s.klasse_id ?? null} vorname={s.name.split(' ')[0]} notizen={profilNotizen} fehler={notizenFehler} onErneut={ladeProfilNotizen} />
        )}

                </div>{/* end ld-profil-work */}
        </div>{/* end ld-tab-panel */}

        {/* ── Modal: Vorjahresnote erfassen (außerhalb ld-tab-panel) ── */}
        <LdSheet open={vjModalOffen} onClose={() => setVjModalOffen(false)} maxWidth={440} kopf={{ titel: 'Vorjahresnote erfassen', kontext: s.name }}>
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--ld-text-secondary)', letterSpacing: 0.4, marginBottom: 6, display: 'block' }}>Fach</label>
                <input
                  value={vjFach} onChange={e => setVjFach(e.target.value)}
                  placeholder="z.B. Deutsch"
                  list="vj-faecher-list"
                  style={{ width: '100%', padding: '10px 13px', borderRadius: 10, border: '1.5px solid var(--ld-outline-soft)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                />
                <datalist id="vj-faecher-list">
                  {Array.from(new Set(dbNoten.map(n => n.fach))).map(f => <option key={f} value={f} />)}
                </datalist>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 2 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--ld-text-secondary)', letterSpacing: 0.4, marginBottom: 6, display: 'block' }}>Schuljahr</label>
                  <LdAuswahl value={vjSchuljahr} onChange={e => setVjSchuljahr(e.target.value)}>
                    {vjSchuljahre.map(sj => <option key={sj} value={sj}>{sj}</option>)}
                  </LdAuswahl>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--ld-text-secondary)', letterSpacing: 0.4, marginBottom: 6, display: 'block' }}>Halbjahr</label>
                  <LdAuswahl value={vjHalbjahr} onChange={e => setVjHalbjahr(Number(e.target.value) as 1 | 2)}>
                    <option value={1}>HJ 1</option>
                    <option value={2}>HJ 2</option>
                  </LdAuswahl>
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--ld-text-secondary)', letterSpacing: 0.4, marginBottom: 6, display: 'block' }}>Zeugnisnote (1–6)</label>
                <LdAuswahl value={vjNote} onChange={e => setVjNote(e.target.value)}>
                  {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>Note {n}</option>)}
                </LdAuswahl>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--ld-text-secondary)', letterSpacing: 0.4, marginBottom: 6, display: 'block' }}>AV-Note (optional)</label>
                  <LdAuswahl value={vjAvNote} onChange={e => setVjAvNote(e.target.value)}>
                    <option value="">—</option>
                    {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
                  </LdAuswahl>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--ld-text-secondary)', letterSpacing: 0.4, marginBottom: 6, display: 'block' }}>SV-Note (optional)</label>
                  <LdAuswahl value={vjSvNote} onChange={e => setVjSvNote(e.target.value)}>
                    <option value="">—</option>
                    {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
                  </LdAuswahl>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <Button variant="secondary" onClick={() => setVjModalOffen(false)} style={{ flex: 1 }}>Abbrechen</Button>
              <Button variant="primary" onClick={vorjahresnoteEinreichen} loading={vjSpeichern} disabled={vjSpeichern || !vjFach.trim()} style={{ flex: 2 }}>Speichern</Button>
            </div>
          </div>
        </LdSheet>

        {/* ── Note eintragen / bearbeiten (Welle U6a: gemeinsames NoteSheet) ── */}
        <NoteSheet
          open={noteModalOffen || !!noteEdit}
          note={noteEdit}
          schueler={{ id: s.id, name: s.name, klasse_id: s.klasse_id ?? null }}
          faecher={verfuegbareFaecher}
          vorbelegtFach={noteFach}
          halbjahr={noteHalbjahr}
          schuljahr={noteSchuljahr}
          onClose={() => { setNoteModalOffen(false); setNoteEdit(null); }}
          onWiederOeffnen={() => { const n = letzteNoteRef.current; if (n) setNoteEdit(n); }}
          onGespeichert={(n, neu) => {
            setDbNoten(prev => neu ? [...prev, n] : prev.map(x => x.id === n.id ? { ...x, ...n } : x));
            setNoteModalOffen(false); setNoteEdit(null); ub.neuLaden();
          }}
          onGeloescht={id => { setDbNoten(prev => prev.filter(x => x.id !== id)); ub.neuLaden(); }}
        />

        {/* ── Welle P1b: Notiz und Mitarbeit-Woche aus den Schnellaktionen ── */}
        {notizNeuOffen && <NotizSheet notiz={null} vorbelegtSchuelerId={s.id} onClose={() => setNotizNeuOffen(false)} />}
        <MitarbeitWocheSheet open={!!mitarbeitWoche} schuelerId={s.id} schuelerName={s.name}
          faecher={ub.daten?.kopf.faecher_bei_mir ?? []} startFach={mitarbeitWoche?.fach || undefined} startWoche={mitarbeitWoche?.woche}
          onClose={() => setMitarbeitWoche(null)}
          onGespeichert={() => ub.neuLaden()} />
        <EintragenSheet open={eintragenOffen} vorname={vorname} onClose={() => setEintragenOffen(false)}
          onWahl={eintragenWahl} onErzaehlen={eintragenErzaehlen} />

        {/* ── Modal: Neuer offener Punkt (global, außerhalb der Tab-Panels) ── */}
        <LdSheet open={neuerPunktModal} onClose={() => setNeuerPunktModal(false)} title="Punkt hinzufügen" ariaLabel="Neuen Punkt erfassen">
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--ld-text-secondary)', letterSpacing: .4, display: 'block', marginBottom: 5 }}>Typ</label>
            <LdAuswahl value={npTyp} onChange={e => setNpTyp(e.target.value)}>
              <option value="nachschreiben">Nachschreiben</option>
              <option value="unterschrift_fehlt">Unterschrift fehlt</option>
              <option value="sonstiges">Sonstiges</option>
            </LdAuswahl>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--ld-text-secondary)', letterSpacing: .4, display: 'block', marginBottom: 5 }}>Beschreibung</label>
            <input value={npTitel} onChange={e => setNpTitel(e.target.value)} placeholder={npTyp === 'nachschreiben' ? 'z.B. Mathe KA' : 'Beschreibung'} style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid var(--ld-outline-soft)', fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }} />
          </div>
          {npTyp === 'nachschreiben' && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--ld-text-secondary)', letterSpacing: .4, display: 'block', marginBottom: 5 }}>Fach</label>
              <input value={npFach} onChange={e => setNpFach(e.target.value)} placeholder="Mathematik" style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid var(--ld-outline-soft)', fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }} />
            </div>
          )}
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--ld-text-secondary)', letterSpacing: .4, display: 'block', marginBottom: 5 }}>Fällig bis (optional)</label>
            <LdDatum value={npFaellig} onChange={e => setNpFaellig(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="secondary" onClick={() => setNeuerPunktModal(false)} style={{ flex: 1 }}>Abbrechen</Button>
            <Button variant="primary" onClick={speichereNeuenPunkt} loading={npSpeichern} disabled={npSpeichern || !npTitel.trim()} style={{ flex: 2 }}>Speichern</Button>
          </div>
        </LdSheet>

      </div>
      {isMobile && <AktionsLeiste vorname={vorname} onErzaehlen={ueberKindErzaehlen} rechts={leistenKnopf} />}
    </div>
  );
}

// Welle P1d: Reiter am PC — nur Text, alle sichtbar
const PC_TABS: { key: ProfilTab; label: string }[] = [
  { key: 'ueberblick', label: 'Überblick' }, { key: 'noten', label: 'Noten' }, { key: 'fehlzeiten', label: 'Fehlzeiten' },
  { key: 'mitarbeit', label: 'Mitarbeit' }, { key: 'gespraeche', label: 'Gespräche' }, { key: 'verlauf', label: 'Verlauf' },
];
const BEREICH_TITEL: Record<ProfilTab, string> = {
  ueberblick: 'Überblick', noten: 'Noten', fehlzeiten: 'Fehlzeiten', mitarbeit: 'Mitarbeit',
  gespraeche: 'Gespräche', verlauf: 'Verlauf', eltern: 'Eltern', notizen: 'Notizen',
};
/** Welle P1d: Kopf einer Bereich-Seite am Handy — „‹ Vorname Nachname“, Titel, eine Zeile darunter */
function BereichKopf({ name, tab, daten, onZurueck }: { name: string; tab: ProfilTab; daten: Ueberblick | null; onZurueck: () => void }) {
  const hj = daten?.halbjahr, b = daten?.bereiche;
  const sjKurz = hj ? schuljahrKurz(hj.schuljahr) : '';
  const unter: Partial<Record<ProfilTab, string>> = hj ? {
    noten: `Halbjahr ${hj.halbjahr}, Schuljahr ${sjKurz}`,
    fehlzeiten: `Schuljahr ${daten!.fehlzeiten.schuljahr}`,
    mitarbeit: `Halbjahr ${hj.halbjahr}, Schuljahr ${sjKurz}`,
    gespraeche: b ? `Schuljahr ${b.notizen.schuljahr}, ${b.gespraeche.im_schuljahr === 1 ? '1 Gespräch' : `${b.gespraeche.im_schuljahr} Gespräche`}` : undefined,
    eltern: daten!.eltern.length === 1 ? '1 Kontakt' : `${daten!.eltern.length} Kontakte`,
    notizen: b ? `${b.notizen.im_schuljahr} in diesem Schuljahr` : undefined,
    verlauf: 'Alles der Reihe nach',
  } : {};
  return (
    <header className="ld-pb">
      <button type="button" className="ld-pb__zurueck" onClick={onZurueck}><MIcon name="chevron_left" size={20} />{name}</button>
      <h2 className="ld-pb__titel">{BEREICH_TITEL[tab]}</h2>
      {unter[tab] && <p className="ld-pb__unter">{unter[tab]}</p>}
    </header>
  );
}
